import { FC, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { ScheduleBlockEntryForm } from "Bands/components";
import {
  BAND_ACTIONS,
  IScheduleBlockEntry,
  IScheduleBlockEntryParams,
  IScheduleBlockEntryProperties,
} from "Bands/types";
import {
  clearActionMeta,
  createEventScheduleBlockEntry,
  dataMetaSelector,
  dispatchGetEventScheduleBlock,
} from "Bands/state";
import { IAPIState, ICardTheme, IFormButtons, IStatus } from "shared/types";
import { copy } from "shared/assets";
import { useBandRoute } from "Bands/utils";
import { addTemporaryToast } from "shared/components/Toast";
import { Card, Title } from "shared/components";
import { Spacer } from "shared/layout";
import { getRegionalSettingsOptions } from "shared/utils";
import { useBandContext, useEventContext } from "Bands/contexts";
import { API_ENDPOINTS } from "shared/constants";

const scheduleBlockCopy = copy.scheduleBlock.edit;

interface CreateScheduleBlockEntryFormProps {
  blockId: string;
  showEditEntryModal: IScheduleBlockEntry | null;
}

export const CreateScheduleBlockEntryForm: FC<
  CreateScheduleBlockEntryFormProps
> = ({ blockId, showEditEntryModal }) => {
  const dispatch = useDispatch();
  const { api: apiMeta } = useSelector(
    dataMetaSelector(BAND_ACTIONS.createEventScheduleBlockEntry)
  );
  const { pushRoute } = useBandRoute();
  const [lastStartDate, setLastStartDate] = useState<string | undefined>(
    undefined
  );
  const [lastEndDate, setLastEndDate] = useState<string | undefined>(undefined);
  const { band } = useBandContext();

  useEffect(() => {
    if (apiMeta?.status === IAPIState.succeeded) {
      dispatch(clearActionMeta(BAND_ACTIONS.createEventScheduleBlockEntry));

      dispatchGetEventScheduleBlock(dispatch, blockId);

      dispatch(
        addTemporaryToast({
          status: IStatus.success,
          message: scheduleBlockCopy.messages.added,
        })
      );
    }
  }, [apiMeta, pushRoute, dispatch, blockId]);

  const handleSubmit = ({
    entry,
    startDate,
    endDate,
  }: {
    entry: IScheduleBlockEntryParams;
    startDate?: string;
    endDate?: string;
  }) => {
    dispatch(
      createEventScheduleBlockEntry({
        ...entry,
        scheduleBlock: API_ENDPOINTS.EVENT_SCHEDULE_BLOCK(blockId),
      })
    );

    startDate && setLastStartDate(startDate);
    endDate && setLastEndDate(endDate);
  };

  const buttons: IFormButtons = useMemo(
    () =>
      ({
        primary: {
          children: scheduleBlockCopy.buttons.add,
          "data-testid": "add_entry",
          tabIndex: 8,
        },
      } as IFormButtons),
    []
  );

  const { event } = useEventContext();
  const { defaultTimeZone } = getRegionalSettingsOptions();

  const defaultValues: IScheduleBlockEntryProperties = useMemo(
    () => ({
      startDate: event?.date,
      endDate: event?.date,
      startTimezone: event?.timeZone || band?.timeZone || defaultTimeZone,
      endTimezone: event?.timeZone || band?.timeZone || defaultTimeZone,
    }),
    [event?.date, event?.timeZone, band?.timeZone, defaultTimeZone]
  );

  return (
    <Card theme={ICardTheme.secondary}>
      <Spacer size="l">
        <Title asComponent="h3" size="m">
          {scheduleBlockCopy.addEntry}
        </Title>
        <ScheduleBlockEntryForm
          error={!!apiMeta?.error}
          apiStatus={apiMeta?.status}
          handleSubmit={handleSubmit}
          buttons={buttons}
          apiError={apiMeta?.error}
          resetForm
          defaultValues={defaultValues}
          lastStartEndDate={{
            startDate: lastStartDate,
            endDate: lastEndDate,
          }}
          showEditEntryModal={showEditEntryModal}
        />
      </Spacer>
    </Card>
  );
};
